import classNames from 'classnames/bind';
import { useLayoutEffect, useRef, useState } from 'react';

import { Telelecope } from '../../assets/icons/Icons';
import Button from '../Button/Button';
import styles from './notFound.module.scss';

const cx = classNames.bind(styles);

interface Props {
  /** Adds padding when nav bar is present */
  hasNav?: boolean;
  /** Path to navigate to */
  href?: string;
  /** Overrides default title of the 404 page */
  title?: string;
  /** Overrides default detail text of the 404 page */
  details?: string;
  hideButton?: boolean;
  /** Renders inside a page layout: fills the viewport space left below it rather than the whole viewport */
  inline?: boolean;
}

const defaultProps: { [key: string]: string } = {
  href: '/',
  title: 'We couldn’t find this page',
  details:
    'We can’t seem to find the page you’re looking for. Try going back to the previous page or return to the dashboard.',
};

const NotFound = ({
  hasNav = false,
  href = defaultProps.href,
  title = defaultProps.title,
  details = defaultProps.details,
  hideButton = false,
  inline = false,
}: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const [minHeight, setMinHeight] = useState<number>();

  // A page's header and breadcrumb sit above an inline 404 and its padding below, so the space to centre in is the
  // viewport less both. The ancestors have no definite height for a percentage to resolve against, hence measuring.
  useLayoutEffect(() => {
    if (!inline) {
      return;
    }
    const fit = () => {
      const el = ref.current;
      if (!el) {
        return;
      }
      const rect = el.getBoundingClientRect();
      const below = document.body.getBoundingClientRect().bottom - rect.bottom;
      setMinHeight(Math.max(window.innerHeight - (rect.top + window.scrollY) - below, 0));
    };
    // The header above can settle after mount, so refit whenever the page around it changes size.
    const observer = new ResizeObserver(fit);
    observer.observe(document.body);
    window.addEventListener('resize', fit);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', fit);
    };
  }, [inline]);

  return (
    <div
      ref={ref}
      className={cx('not-found', 'page-bg', { 'has-nav': hasNav, 'not-found--inline': inline })}
      style={inline ? { minHeight } : undefined}
      data-testid='not-found'
    >
      {Telelecope}
      <h1 className={cx('not-found__title')}>{title}</h1>
      <p className={cx('not-found__details')}>{details}</p>
      {!hideButton && (
        <Button className={cx('not-found__button')} href={href} type='link-primary' size='large'>
          Return to Dashboard
        </Button>
      )}
    </div>
  );
};

export default NotFound;
